import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import { Breadcrumbs, Divider, Link, Stack } from '@packages/shared';
import { useConfig } from '@packages/utilities';
import { ExtendedBreadcrumbsBackButton } from './ExtendedBreadcrumbsBackButton';

// eslint-disable-next-line @typescript-eslint/naming-convention
const BreadcrumbsFragment = graphql(/* GraphQL */ `
  fragment BreadcrumbsFragment on Breadcrumb {
    breadCrumb {
      name
      id
      uriPath
    }
  }
`);

export type ExtendedBreadcrumbsProps = {
  /** The breadcrumbs object. */
  breadCrumbData: FragmentType<typeof BreadcrumbsFragment> | undefined | null;
};

/**
 * <p>This components provides a basic breadcrumbs navigation, to get back to the previous page.</p>
 * <p>In mobile, the breadcrumbs are condensed to 3 entries maximum.</p>
 */
export const ExtendedBreadcrumbs = ({ breadCrumbData }: ExtendedBreadcrumbsProps) => {
  const {
    host: { domain },
  } = useConfig();

  const breadCrumb = breadCrumbData
    ? getFragmentData(BreadcrumbsFragment, breadCrumbData).breadCrumb ?? []
    : [];

  const hasBreadcrumb = breadCrumb.length > 0;

  return (
    <>
      <Stack gap={1} direction="row" alignItems="center">
        <ExtendedBreadcrumbsBackButton breadCrumbData={breadCrumb} />
        {hasBreadcrumb && (
          <>
            <Breadcrumbs>
              {breadCrumb.map(({ id, uriPath, name }) => (
                <Link key={id} href={uriPath} underline="hover">
                  {name}
                </Link>
              ))}
            </Breadcrumbs>
            <script
              type="application/ld+json"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  itemListElement: breadCrumb.map(({ uriPath, name }, index) => ({
                    item: { name, '@id': `https://www.${domain}${uriPath}` },
                    '@type': 'ListItem',
                    position: index + 1,
                  })),
                  '@type': 'BreadcrumbList',
                  '@context': 'http://schema.org',
                }),
              }}
            />
          </>
        )}
      </Stack>
      <Divider sx={{ mt: 1.5, mb: 1 }} />
    </>
  );
};
